import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Spinner, apiClient, ApiClientError, useToast, formatBps, bpsToPercent, percentToBps } from '@dogdisk/shared'
import { AgentLayout } from '../components/agent-layout'
import { usePromoInfo, type PromoNeighbor } from '../_lib/agent-api'

export function AgentSubAgentSettingsPage() {
  const { data, isLoading } = usePromoInfo()
  const [editingId, setEditingId] = useState<string | null>(null)

  if (isLoading) {
    return (
      <AgentLayout>
        <div className="p-6">
          <Spinner className="text-amber-500" />
        </div>
      </AgentLayout>
    )
  }

  if (!data) {
    return (
      <AgentLayout>
        <div className="p-6 text-sm text-gray-500">加载失败</div>
      </AgentLayout>
    )
  }

  // L3 无下级
  if (data.level >= 3) {
    return (
      <AgentLayout>
        <div className="p-6">
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-800">
            您当前是 L{data.level} 代理，没有下级代理；如需扩展请联系上级或管理员。
          </div>
        </div>
      </AgentLayout>
    )
  }

  return (
    <AgentLayout>
      <div data-testid="B7KvU" className="flex flex-col gap-5 p-6">
        {/* 只显示下级代理列表(对齐 B7KvU;info 卡已删) */}
        <section className="rounded-xl border border-gray-200 bg-white p-5">
          <h2 className="pb-3 text-sm font-semibold text-gray-900">下级代理列表</h2>
          <div className="overflow-hidden">
            <div className="grid grid-cols-[300px_130px_60px_1fr_60px_90px] border-b border-gray-200 py-2.5 text-xs font-semibold text-gray-500">
              <span>UUID</span>
              <span>代理 Username</span>
              <span>级别</span>
              <span>分成比例</span>
              <span>下级数</span>
              <span>操作</span>
            </div>

            {data.sub_agents.map((s) => {
              const rowKey = s.user_id ?? s.user_id_masked
              const isEditing = editingId === rowKey
              return (
                <div key={rowKey}>
                  <div
                    className={[
                      'grid grid-cols-[300px_130px_60px_1fr_60px_90px] items-center py-3 text-[13px] border-b border-gray-100',
                      isEditing ? 'bg-amber-50' : '',
                    ].join(' ')}
                  >
                    <span className="truncate font-mono text-xs text-gray-400" title={s.user_id}>
                      {s.user_id ?? s.user_id_masked}
                    </span>
                    <span
                      className={`text-gray-700 ${isEditing ? 'font-semibold' : ''}`}
                    >
                      {s.username_masked}
                    </span>
                    <span className="text-gray-700">{s.level}级</span>
                    <span className="font-semibold text-amber-500">
                      {formatBps(s.rate_bps)}
                    </span>
                    <span className="text-gray-700">{s.first_agent_count ?? 0}</span>
                    <span>
                      {isEditing ? (
                        <button
                          type="button"
                          onClick={() => setEditingId(null)}
                          className="text-xs text-red-500 hover:underline"
                        >
                          收起
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setEditingId(rowKey)}
                          className="text-xs text-blue-500 hover:underline"
                        >
                          调整比例
                        </button>
                      )}
                    </span>
                  </div>

                  {isEditing && (
                    <RateEditor
                      sub={s}
                      maxRate={bpsToPercent(data.rate_bps)}
                      onClose={() => setEditingId(null)}
                    />
                  )}
                </div>
              )
            })}

            {data.sub_agents.length === 0 && (
              <p className="py-12 text-center text-sm text-gray-400">暂无直接下级代理</p>
            )}
          </div>
        </section>
      </div>
    </AgentLayout>
  )
}

function RateEditor({
  sub,
  maxRate,
  onClose,
}: {
  sub: PromoNeighbor
  maxRate: number
  onClose: () => void
}) {
  const qc = useQueryClient()
  const toast = useToast()
  const [rate, setRate] = useState<number>(bpsToPercent(sub.rate_bps ?? 0))
  const [error, setError] = useState<string | null>(null)

  const adjust = useMutation({
    mutationFn: ({ uid, rateBps }: { uid: string; rateBps: number }) =>
      apiClient.patch(`/gapi/agent/sub-agents/${uid}/rate`, {
        rate_bps: rateBps,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['agent', 'promo'] })
      toast.push('费率已更新', { variant: 'success' })
      onClose()
    },
    onError: (e) => {
      if (e instanceof ApiClientError) setError(`${e.code}: ${e.message}`)
      else setError(e instanceof Error ? e.message : '提交失败')
    },
  })

  const onSave = () => {
    const uid = sub.user_id
    if (!uid) {
      setError('缺少下级代理 user_id，无法调整')
      return
    }
    // 严格级联(2026-07-01):下级比例须 < 本代理比例(maxRate),不得相等或超过。
    if (rate < 0 || rate >= maxRate) {
      setError(`费率必须 ≥ 0% 且 < 本代理 ${maxRate}%`)
      return
    }
    const rateBps = percentToBps(rate)
    if (rateBps == null) {
      setError('费率必须在 [0, 100]% 内')
      return
    }
    setError(null)
    adjust.mutate({ uid, rateBps })
  }

  const diff = maxRate - rate

  return (
    <div
      className="flex flex-col gap-3 border-y border-amber-200 px-5 py-4"
      style={{ backgroundColor: '#FFFBEB' }}
    >
      <div className="flex items-center justify-between">
        <span className="text-[13px] font-semibold text-gray-700">
          调整 {sub.username_masked} 的分成比例
        </span>
        <span
          className="rounded px-2 py-0.5 text-xs font-medium"
          style={{ backgroundColor: 'rgba(245,158,11,0.10)', color: '#B45309' }}
        >
          须 &lt; {maxRate}%
        </span>
      </div>

      <div className="flex items-center gap-4">
        <span className="text-xs text-gray-400">0%</span>
        <input
          type="range"
          min={0}
          max={maxRate}
          step={1}
          value={rate}
          onChange={(e) => setRate(Number(e.target.value))}
          className="flex-1 accent-amber-500"
        />
        <span className="text-xs text-gray-400">{maxRate}%</span>
        <div className="flex items-center gap-1 rounded border border-gray-200 bg-white px-2.5 py-1">
          <input
            type="number"
            min={0}
            max={maxRate}
            value={rate}
            onChange={(e) => setRate(Number(e.target.value))}
            className="w-12 bg-transparent text-right text-sm font-semibold text-amber-600 outline-none"
          />
          <span className="text-xs text-gray-500">%</span>
        </div>
      </div>

      <div className="flex items-end justify-between gap-3">
        <div className="flex flex-col gap-2">
          <span className="text-xs text-gray-500">
            差额 {diff}%（{maxRate}% − {rate}%）归我所得
          </span>
          {error && <span className="text-xs text-red-600">{error}</span>}
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 bg-white px-4 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
          >
            取消
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={adjust.isPending}
            className="rounded-md px-4 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
            style={{ backgroundColor: '#F59E0B' }}
          >
            {adjust.isPending ? '保存中…' : '保存'}
          </button>
        </div>
      </div>
    </div>
  )
}
